import type React from 'react';

type ObserveScrollOptions = {
  container: HTMLElement | Window | string;
  callback: () => void;
  offset?: number;
};

export function observeScrollToBottom({
                                        container,
                                        callback,
                                        offset = 100,
                                      }: ObserveScrollOptions): () => void {
  const resolveContainer = (): HTMLElement | Window | null => {
    if (typeof container === 'string') {
      if (container instanceof Window) return window;
      return document.querySelector(container);
    }
    return container;
  };

  const target = resolveContainer();
  if (!target) {
    console.warn('[observeScrollToBottom] Container not found:', container);
    return () => {};
  }

  const handler = () => {
    let scrollTop: number, scrollHeight: number, clientHeight: number;

    if (target instanceof Window) {
      scrollTop = window.scrollY;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    } else {
      scrollTop = target.scrollTop;
      scrollHeight = target.scrollHeight;
      clientHeight = target.clientHeight;
    }

    if (scrollHeight - scrollTop - clientHeight <= offset) {
      callback();
    }
  };

  const eventTarget = target instanceof Window ? window : target;
  eventTarget.addEventListener('scroll', handler);

  return () => {
    eventTarget.removeEventListener('scroll', handler);
  };
}

export function getMaxZIndexInDrawers (excludeClassName?: string) {
  const drawers = document.querySelectorAll('.ant-drawer');
  let maxZ = -Infinity;
  let maxEl = null;

  drawers.forEach((el) => {
    if (excludeClassName && el.classList.contains(excludeClassName)) return;
    const z = parseInt(window.getComputedStyle(el).zIndex, 10);
    if (!isNaN(z) && z > maxZ) {
      maxZ = z;
      maxEl = el;
    }
  });

  return { maxZIndex: maxZ, element: maxEl };
}

/**
 * A z-index that clears every open drawer.
 *
 * `@/components/Drawer` gives itself the highest open drawer + 3, well past
 * antd's default 1000 — so a static `Modal.confirm` raised from inside a drawer
 * renders *underneath* it: visible, every click swallowed (the failure mode
 * `docs/drawers.md` warns about). Dialogs a drawer can open pass this instead.
 *
 * Undefined when no drawer is open — antd's own default is right then, and
 * pinning a number would only fight the next thing that stacks.
 */
export function zIndexAboveDrawers(): number | undefined {
  const { maxZIndex } = getMaxZIndexInDrawers();
  return Number.isFinite(maxZIndex) ? maxZIndex + 3 : undefined;
}

// Measured, not class-based: since antd v6 a closed popover stays mounted with
// `display: none` and no `ant-popover-hidden`, so every page read as "popover
// open" and clicks behind it were swallowed.
export function isAnyPopoverOpen() {
  return Array.from(document.querySelectorAll('.ant-popover')).some((el) => {
    if (el.classList.contains('ant-popover-hidden')) return false;
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return false;
    const { display, visibility, opacity } = window.getComputedStyle(el);
    return display !== 'none' && visibility !== 'hidden' && opacity !== '0';
  });
}

// Spread onto a non-native clickable element (div/span rows) so keyboard users
// can reach and trigger it: Enter/Space activate, focus-visible styling comes
// from the element's own stylesheet.
export function pressableProps(handler: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handler();
      }
    },
  };
}
